
import { Pencil, X, RefreshCw, Check, ScrollText, BookOpen } from "lucide-react";
import type { CommentResponse, RuleResponse, KnowledgeItemResponse } from "../../gen/models";

interface ThreadCommentEditorProps {
  comment: CommentResponse;
  rulesList: RuleResponse[];
  knowledgeList: KnowledgeItemResponse[];
  editingText: string;
  editingRuleIds: string[];
  editingKnowledgeIds: string[];
  savingEdit: boolean;
  mentionType: "rule" | "knowledge" | null;
  mentionQuery: string;
  mentionTarget: "composer" | "editor" | null;
  onCancelEdit: () => void;
  onSaveEdit: (commentId: string) => void;
  onEditTextChange: (text: string) => void;
  onSelectMentionRule: (rule: RuleResponse) => void;
  onSelectMentionKnowledge: (knowledge: KnowledgeItemResponse) => void;
  onRemoveEditingRule: (ruleId: string) => void;
  onRemoveEditingKnowledge: (knowledgeId: string) => void;
}

export function ThreadCommentEditor({
  comment: c,
  rulesList,
  knowledgeList,
  editingText,
  editingRuleIds,
  editingKnowledgeIds,
  savingEdit,
  mentionType,
  mentionQuery,
  mentionTarget,
  onCancelEdit,
  onSaveEdit,
  onEditTextChange,
  onSelectMentionRule,
  onSelectMentionKnowledge,
  onRemoveEditingRule,
  onRemoveEditingKnowledge,
}: ThreadCommentEditorProps) {
  return (
    <div
      id={`comment-card-${c.id}`}
      className="p-3 rounded-xl bg-white dark:bg-stone-900 border border-stone-300 dark:border-amber-400/50 shadow-md space-y-2.5 animate-in fade-in duration-150"
    >
      <div className="flex items-center justify-between text-[11px] font-semibold text-stone-800 dark:text-stone-300">
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <Pencil className="w-3 h-3" />
          <span>Edit Comment</span>
        </span>
        <button
          type="button"
          onClick={onCancelEdit}
          className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>



      <div className="relative">
        <textarea
          autoFocus
          rows={3}
          value={editingText}
          onChange={(e) => onEditTextChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onSaveEdit(c.id);
            }
          }}
          placeholder="Edit comment... Type @rule: or @knowledge: to mention"
          className="w-full rounded-xl bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 p-2 text-xs text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-hidden focus:ring-1 focus:ring-amber-500/50 resize-none transition"
        />

        {mentionType && mentionTarget === "editor" && (
          <div className="absolute left-0 bottom-full mb-1 z-60 w-full bg-white dark:bg-stone-900 rounded-xl shadow-xl ring-1 ring-stone-200 dark:ring-stone-800 max-h-48 overflow-y-auto p-1 text-xs">
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-800">
              Mention {mentionType === "rule" ? "Rule" : "Knowledge Item"}
            </div>
            {mentionType === "rule" ? (
              rulesList
                .filter(
                  (r) =>
                    r.title.toLowerCase().includes(mentionQuery) ||
                    r.category.toLowerCase().includes(mentionQuery)
                )
                .slice(0, 5)
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onSelectMentionRule(r)}
                    className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition flex items-center justify-between gap-2"
                  >
                    <span className="font-medium text-stone-800 dark:text-stone-200 truncate">
                      {r.title}
                    </span>
                    <span className="text-[9px] uppercase font-mono px-1 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">
                      {r.category}
                    </span>
                  </button>
                ))
            ) : (
              knowledgeList
                .filter(
                  (k) =>
                    k.title.toLowerCase().includes(mentionQuery) ||
                    k.category.toLowerCase().includes(mentionQuery)
                )
                .slice(0, 5)
                .map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => onSelectMentionKnowledge(k)}
                    className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition flex items-center justify-between gap-2"
                  >
                    <span className="font-medium text-stone-800 dark:text-stone-200 truncate">
                      {k.title}
                    </span>
                    <span className="text-[9px] uppercase font-mono px-1 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">
                      {k.category}
                    </span>
                  </button>
                ))
            )}
          </div>
        )}
      </div>

      {/* Mentions Chips */}
      {(editingRuleIds.length > 0 || editingKnowledgeIds.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {editingRuleIds.map((rid) => {
            const r = rulesList.find((x) => x.id === rid);
            return (
              <span
                key={rid}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200"
              >
                <ScrollText className="w-2.5 h-2.5" />
                <span>{r?.title || rid.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => onRemoveEditingRule(rid)}
                  className="hover:text-amber-900 dark:hover:text-amber-100"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          {editingKnowledgeIds.map((kid) => {
            const k = knowledgeList.find((x) => x.id === kid);
            return (
              <span
                key={kid}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-200"
              >
                <BookOpen className="w-2.5 h-2.5" />
                <span>{k?.title || kid.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => onRemoveEditingKnowledge(kid)}
                  className="hover:text-indigo-900 dark:hover:text-indigo-100"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancelEdit}
          className="px-2.5 py-1 text-xs text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 font-medium transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSaveEdit(c.id)}
          disabled={savingEdit || !editingText.trim()}
          className="px-3 py-1 rounded-lg bg-stone-900 hover:bg-stone-800 text-white dark:bg-stone-100 dark:hover:bg-white dark:text-stone-900 font-semibold text-xs transition flex items-center gap-1 disabled:opacity-40"
        >
          {savingEdit ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          <span>Save</span>
        </button>
      </div>
    </div>
  );
}
